import { useMemo } from "react"

import { useAPIHealth } from "shared/api/health/useAPIHealth"

import { useProjectData } from "entities/projects/api/useProjectData"
import { useInflationSurchargeQuery } from "entities/projects/graphql/inflation-surcharge/queries.generated"
import { usePriceListsQuery } from "entities/projects/graphql/price-lists/queries.generated"
import { getProductPositionTotal } from "entities/projects/lib/getProductPositionTotal"
import { getProductTotal } from "entities/projects/lib/getProductTotal"
import { useProjectDetailsHref } from "entities/projects/model/useProjectDetailsHref"
import { useTeamQuery } from "entities/teams/graphql/queries.generated"

import { useProjectPhases } from "../model/useProjectPhases"
import { AggregatedProjectData } from "../types/Project"
import { useProjectAttachmentsData } from "./useProjectAttachmentsData"
import { useProjectPrice } from "./useProjectPrice"

export interface AggregatedProjectDataOptions {
  includeChildren?: boolean
  includePrice?: boolean
  includeTeam?: boolean
  includeAttachments?: boolean
}

export function useAggregatedProjectData({
  projectID,
  options
}: {
  projectID: string
  options?: AggregatedProjectDataOptions
}): AggregatedProjectData {
  const {
    project,
    isLoading,
    isPending,
    isUpdatingChildren,
    permissions,
    role,
    isLoadingPermissions,
    error
  } = useProjectData(projectID, options?.includeChildren)

  const { price: isPriceServiceAvailable } = useAPIHealth()
  const { projectPhases, isLoading: isLoadingProjectPhases } =
    useProjectPhases()
  const {
    data: inflationSurchargeData,
    loading: isLoadingStandardInflationSurcharge
  } = useInflationSurchargeQuery()

  const {
    price,
    isLoading: isLoadingPrice,
    isUpdating: isUpdatingPrice
  } = useProjectPrice({
    projectID,
    skip: !options?.includePrice
  })
  const { data: priceListData, loading: isLoadingPriceLists } =
    usePriceListsQuery()

  const { data: teamData, loading: isLoadingTeam } = useTeamQuery({
    variables: { id: project?.team?.id ?? "" },
    skip: !project?.team?.id || !options?.includeTeam
  })

  const href = useProjectDetailsHref(projectID)

  const productTotal = useMemo(
    () => (project?.children ? getProductTotal(project.children) : undefined),
    [project?.children]
  )

  const productPositionTotal = useMemo(
    () =>
      project?.children ? getProductPositionTotal(project.children) : undefined,
    [project?.children]
  )

  const { attachments, attachmentCategories } = useProjectAttachmentsData({
    projectID,
    options: { skip: !options?.includeAttachments }
  })

  return {
    project: {
      data: project,
      isLoading,
      isUpdating: isUpdatingChildren,
      error: !!error
    },
    team: options?.includeTeam
      ? {
          data: teamData?.team,
          isLoading: isLoadingTeam,
          isUpdating: !!teamData?.team && isLoadingTeam
        }
      : null,
    price: options?.includePrice
      ? {
          data: price,
          isLoading: isLoadingPrice,
          isUpdating: isUpdatingPrice,
          error: !isPriceServiceAvailable
        }
      : null,
    phases: { data: projectPhases, isLoading: isLoadingProjectPhases },
    permissions: { data: permissions, isLoading: isLoadingPermissions },
    role: { data: role, isLoading: isLoadingPermissions },
    priceLists: {
      data: priceListData?.priceLists,
      isLoading: isLoadingPriceLists
    },
    standardInflationSurcharge: {
      data: inflationSurchargeData?.inflationSurcharge.value,
      isLoading: isLoadingStandardInflationSurcharge
    },
    attachments: options?.includeAttachments ? attachments : null,
    attachmentCategories: options?.includeAttachments
      ? attachmentCategories
      : null,

    meta: { isPending, href, productTotal, productPositionTotal }
  }
}
